import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { PintGlassEmptyIcon, PintGlassFullIcon, PintGlassHalfIcon } from '../icons/PintGlassIcons';
import styles from './OnboardingTour.module.css';
import { OnboardingTourContext } from '../../state/hooks/useOnboardingTour';
import { useAppSettings } from '../../state/providers/AppSettingsProvider';

interface TourStep {
  id: string;
  title: string;
  content: React.ReactNode;
  targetSelector?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  icon?: React.ReactNode;
}

interface OnboardingTourProps {
  onComplete: () => void;
}

// Steps that highlight beer cards - only these should trigger scrolling to position the card
const BEER_CARD_STEPS = ['tasting-list-badge', 'badges'];

// Steps that highlight elements inside the beer card (rating area)
// These should NOT scroll - the card is already in position from previous step
const NO_SCROLL_STEPS: string[] = [];

const buildTourSteps = (
  festivalName: string,
  isBrewletteEnabled: boolean,
  actionControlsMode: 'bar' | 'fab',
  badgePosition: 'top_right' | 'number_column',
  availabilityStyle: 'default' | 'glass'
): TourStep[] => [
  {
    id: 'welcome',
    title: `Welcome to ${festivalName}! 🍺`,
    content: (
      <Box>
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, fontSize: '1.05rem' }}>
          Let's show you a few handy tricks to make the most of your festival experience!
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>
          This'll only take a minute, promise.
        </Typography>
      </Box>
    ),
    position: 'center',
  },
  {
    id: 'navigation-tabs',
    title: 'All Beers vs My Beers',
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          Switch between <strong>All Beers</strong> (everything at the festival) and <strong>My Beers</strong> (stuff you've rated or favourited) using the tabs at the bottom.
        </Typography>
      </Box>
    ),
    targetSelector: '[data-tour="navigation-tabs"]',
    position: 'top',
  },
  {
    id: 'selection-actions',
    title: 'Quick Actions',
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          Tap a beer card to select it. {actionControlsMode === 'fab' ? (
            <span>Then tap the <strong>action button</strong> to favourite or add it to your tasting list.</span>
          ) : (
            <span>The <strong>action bar</strong> lets you favourite or add it to your tasting list.</span>
          )}
        </Typography>
        {isBrewletteEnabled && (
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
            <strong>Brewlette</strong> picks a random beer from your current list — handy when you can't decide.
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
          Select multiple beers to apply actions to the whole group.
        </Typography>
      </Box>
    ),
    targetSelector: actionControlsMode === 'fab' ? '[data-tour="fab-button"]' : '[data-tour="selection-action-bar"]',
    position: 'bottom',
  },
  {
    id: 'tasting-list-badge',
    title: 'Your Tasting List',
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          {badgePosition === 'number_column' ? (
            <span>See the <strong style={{ color: 'var(--primary-colour)' }}>bookmark icon</strong> in the number column? That means it's in your tasting list!</span>
          ) : (
            <span>See the <strong style={{ color: 'var(--primary-colour)' }}>bookmark icon</strong>? That means it's in your tasting list!</span>
          )}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
          Makes it easy to spot what you've queued up to try.
        </Typography>
      </Box>
    ),
    targetSelector: badgePosition === 'number_column' ? '[data-left-column]' : '[data-tour="beer-card"]',
    position: 'bottom',
  },
  {
    id: 'badges',
    title: 'Beer Status Badges',
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          {badgePosition === 'number_column' ? 'Check the number column:' : 'Check the badge area:'}
        </Typography>
        <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {availabilityStyle === 'glass' ? (
            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <PintGlassFullIcon size={18} colour="#4caf50" />
                <PintGlassHalfIcon size={18} colour="#ffc107" />
                <PintGlassEmptyIcon size={18} colour="#f44336" />
              </Box>
              <span><strong>Availability</strong> - Green/Orange/Red</span>
            </Box>
          ) : (
            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4caf50' }} />
                <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ffc107' }} />
                <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f44336' }} />
              </Box>
              <span><strong>Availability</strong> - Green/Orange/Red</span>
            </Box>
          )}
          <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
            <Box sx={{ width: '28px', height: '28px', borderRadius: '10px', backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 86%, black)', border: '1px solid var(--primary-colour)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary-colour)' }}>4.5</Typography>
            </Box>
            <span><strong>Number badge</strong> - Your rating</span>
          </Box>
          <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
            <FavoriteIcon sx={{ color: '#ff4040', fontSize: '1.05rem' }} />
            <span><strong>Heart</strong> - You've favourited it</span>
          </Box>
        </Box>
      </Box>
    ),
    targetSelector: badgePosition === 'number_column' ? '[data-left-column]' : '[data-tour="beer-card"]',
    position: 'bottom',
  },
  {
    id: 'complete',
    title: "You're All Set! 🎉",
    content: (
      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          That's everything. Now go find some good beer.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Cheers! 🍻
        </Typography>
      </Box>
    ),
    position: 'center',
  },
];

/**
 * OnboardingTour - Interactive guided tour for first-time users
 * Shows spotlight effects on UI elements with explanatory popups
 */
const OnboardingTour: React.FC<OnboardingTourProps> = ({ onComplete }) => {
  const { getLogoUrl, getAppName, isFeatureEnabled } = useAppSettings();
  const welcomeLogoUrl = (getLogoUrl() ?? '').trim();
  const [currentStep, setCurrentStepLocal] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(true);
  
  // Sync step with context so beer cards can show demo icons
  const tourContext = useContext(OnboardingTourContext);

  // Prevent user scroll while tour is active (but allow programmatic scroll)
  useEffect(() => {
    let allowScroll = false;
    
    const preventScroll = (e: Event) => {
      if (!allowScroll) {
        e.preventDefault();
      }
    };
    
    // Block touch and wheel scroll events on the document
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
    
    // Expose a way to temporarily allow scroll for programmatic scrolling
    (window as unknown as { __allowTourScroll: (allow: boolean) => void }).__allowTourScroll = (allow: boolean) => {
      allowScroll = allow;
    };
    
    return () => {
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
      delete (window as unknown as { __allowTourScroll?: (allow: boolean) => void }).__allowTourScroll;
    };
  }, []);
  
  const { getActionControlsMode, getCardBadgePosition, getAvailabilityBadgeStyle } = useAppSettings();
  const TOUR_STEPS = buildTourSteps(
    getAppName(),
    isFeatureEnabled('brewlette'),
    getActionControlsMode(),
    getCardBadgePosition(),
    getAvailabilityBadgeStyle()
  );

  const setCurrentStep = useCallback((stepOrUpdater: number | ((prev: number) => number)) => {
    setCurrentStepLocal(prev => {
      const newStep = typeof stepOrUpdater === 'function' ? stepOrUpdater(prev) : stepOrUpdater;
      // Sync to context if available
      if (tourContext) {
        tourContext.setCurrentStep(newStep);
      }
      return newStep;
    });
  }, [tourContext]);

  const step = TOUR_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  // Helper to temporarily allow scroll for programmatic scrolling
  const allowScrollTemporarily = useCallback((callback: () => void) => {
    const allowFn = (window as unknown as { __allowTourScroll?: (allow: boolean) => void }).__allowTourScroll;
    if (allowFn) allowFn(true);
    callback();
    // Re-lock after a short delay to let scroll complete
    setTimeout(() => {
      if (allowFn) allowFn(false);
    }, 100);
  }, []);

  // Find and measure the target element
  const updateTargetPosition = useCallback(() => {
    if (step.targetSelector) {
      const element = document.querySelector(step.targetSelector);
      if (element) {
        // Only scroll for beer card steps, not for navigation tabs or other UI elements
        const shouldScrollToBeerCard = BEER_CARD_STEPS.includes(step.id);
        const shouldSkipScroll = NO_SCROLL_STEPS.includes(step.id);
        
        // For beer card related steps, scroll to position card at top of viewport
        // This ensures the card is visible and not hidden under the search bar
        if (shouldScrollToBeerCard && !shouldSkipScroll) {
          const rect = element.getBoundingClientRect();
          
          // Position card at top of viewport, accounting for search bar height (~120px)
          const searchBarHeight = 120;
          const targetY = rect.top + window.scrollY - searchBarHeight;
          
          // Only scroll if the card isn't already in the right position
          if (rect.top < searchBarHeight || rect.top > searchBarHeight + 50) {
            allowScrollTemporarily(() => {
              window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
            });
          }
        }
        
        // Small delay to let scroll complete, then measure position
        requestAnimationFrame(() => {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
        });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [step.targetSelector, step.id, allowScrollTemporarily]);

  useEffect(() => {
    // Steps that require DOM changes (accordion expansion, action bar slide-in) need longer delay
    // Other steps can update immediately
    const needsLongerDelay = ['star-rating-step', 'notes-step', 'favourite-button-step', 'selection-actions'].includes(step.id);
    const delay = needsLongerDelay ? 300 : 0;
    
    const timer = setTimeout(updateTargetPosition, delay);
    
    // Update position on scroll/resize
    window.addEventListener('scroll', updateTargetPosition, true);
    window.addEventListener('resize', updateTargetPosition);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updateTargetPosition, true);
      window.removeEventListener('resize', updateTargetPosition);
    };
  }, [updateTargetPosition, currentStep, step.id]);

  // Animate step transitions - minimal delay for snappy feel
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 100);
    return () => clearTimeout(timer);
  }, [currentStep]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  // Tooltip is always anchored just above the bottom nav - CSS handles positioning
  const getTooltipStyle = (): React.CSSProperties => {
    return {};
  };

  // Calculate spotlight clip path
  const getSpotlightStyle = (): React.CSSProperties => {
    if (!targetRect) {
      return {};
    }

    const padding = 8;
    const x = targetRect.left - padding;
    const y = targetRect.top - padding;
    const width = targetRect.width + padding * 2;
    const height = targetRect.height + padding * 2;
    const radius = 12;

    return {
      clipPath: `polygon(
        0% 0%,
        0% 100%,
        ${x}px 100%,
        ${x}px ${y + radius}px,
        ${x + radius}px ${y}px,
        ${x + width - radius}px ${y}px,
        ${x + width}px ${y + radius}px,
        ${x + width}px ${y + height - radius}px,
        ${x + width - radius}px ${y + height}px,
        ${x + radius}px ${y + height}px,
        ${x}px ${y + height - radius}px,
        ${x}px 100%,
        100% 100%,
        100% 0%
      )`,
    };
  };

  // Use full-page layout for the first (welcome) step
  const isWelcomeStep = currentStep === 0;
  // Step 2 (0-indexed) is the quick actions step - move tooltip higher
  const isActionBarStep = step.id === 'selection-actions';
  const tooltipClass = isWelcomeStep 
    ? `${styles.tooltip} ${styles.tooltipFullPage}` 
    : `${styles.tooltip} ${isAnimating ? styles.animating : ''} ${isActionBarStep ? styles.tooltipHigher : ''}`;

  return (
    <Box className={styles.tourOverlay}>
      {/* Dark overlay with spotlight cutout - hide on welcome step */}
      {!isWelcomeStep && (
        <Box 
          className={styles.spotlightOverlay}
          style={targetRect ? getSpotlightStyle() : undefined}
        />
      )}
      
      {/* Spotlight ring around target - hide on welcome step */}
      {!isWelcomeStep && targetRect && (
        <Box
          className={styles.spotlightRing}
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
          }}
        />
      )}

      {/* Tooltip */}
      <Box
        className={tooltipClass}
        style={isWelcomeStep ? {} : getTooltipStyle()}
      >
        {/* Logo for welcome screen */}
        {isWelcomeStep && (
          welcomeLogoUrl ? (
            <img 
              src={welcomeLogoUrl} 
              alt="Beer Festival Logo" 
              className={styles.welcomeLogo}
            />
          ) : null
        )}

        {/* Close/Skip button */}
        {isWelcomeStep ? (
          <Button
            onClick={handleSkip}
            sx={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.85rem',
              textTransform: 'none',
              padding: '4px 8px',
              minWidth: 'auto',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Skip
          </Button>
        ) : (
          <IconButton
            className={styles.closeButton}
            onClick={handleSkip}
            aria-label="Skip tour"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}

        {/* Step indicator */}
        <Box className={styles.stepIndicator}>
          {TOUR_STEPS.map((_, index) => (
            <Box
              key={index}
              className={`${styles.stepDot} ${index === currentStep ? styles.active : ''} ${index < currentStep ? styles.completed : ''}`}
            />
          ))}
        </Box>

        {/* Content */}
        <Typography variant="h6" className={styles.title}>
          {step.title}
        </Typography>
        
        <Box className={styles.content}>
          {step.content}
        </Box>

        {/* Navigation buttons */}
        <Box className={styles.navigation}>
          {!isFirstStep && (
            <Button
              variant="text"
              onClick={handlePrev}
              startIcon={<ArrowBackIcon />}
              className={styles.navButton}
            >
              Back
            </Button>
          )}
          
          <Box sx={{ flex: 1 }} />
          
          {!isLastStep && !isWelcomeStep && (
            <Button
              variant="text"
              onClick={handleSkip}
              className={styles.skipButton}
            >
              Skip Tour
            </Button>
          )}
          
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={!isLastStep ? <ArrowForwardIcon /> : undefined}
            className={styles.nextButton}
          >
            {isLastStep ? "Let's Go!" : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingTour;
